// ── Invite Tab Logic — geração e revogação de código de convite ───────────────
import { getState, getTrip }             from '@/store/tripState';
import { saveTrip }                      from '@/services/sync.service';
import { createInvite, revokeInvite,
         resolveInvite, joinTripViaInvite } from '@/services/invites.service';
import { toastSuccess, toastError }      from '@/ui/toast';
import { logAction }                     from '@/services/logger.service';

export function renderInviteCard(): void {
  const trip        = getTrip();
  const displayEl   = document.getElementById('invite-code-display');
  const emptyEl     = document.getElementById('invite-code-empty');
  const valueEl     = document.getElementById('invite-code-value');
  if (!displayEl || !emptyEl || !valueEl) return;

  if (trip.inviteCode) {
    valueEl.textContent   = trip.inviteCode;
    displayEl.style.display = 'block';
    emptyEl.style.display   = 'none';
  } else {
    displayEl.style.display = 'none';
    emptyEl.style.display   = 'block';
  }
}

export async function generateInviteCode(): Promise<void> {
  const trip   = getTrip();
  const tripId = getState().currentTripId;
  if (!tripId) return;
  try {
    const code = await createInvite(tripId, trip.name ?? 'Viagem');
    await saveTrip('inviteCode', code);
    logAction('invite_created', `Código de convite gerado para trip ${tripId}`, { tripId, code });
    toastSuccess('Código gerado! Compartilhe com quem você quer convidar.');
    renderInviteCard();
  } catch (e) {
    toastError('Erro ao gerar código: ' + (e instanceof Error ? e.message : String(e)));
  }
}

export async function revokeInviteCode(): Promise<void> {
  const trip   = getTrip();
  const tripId = getState().currentTripId;
  if (!trip.inviteCode || !tripId) return;
  const { confirmModal } = await import('@/ui/modal');
  const ok = await confirmModal('Revogar este código? Ninguém mais poderá entrar com ele.');
  if (!ok) return;
  try {
    await revokeInvite(trip.inviteCode);
    await saveTrip('inviteCode', null);
    logAction('invite_revoked', `Código de convite revogado para trip ${tripId}`, { tripId });
    toastSuccess('Código revogado. Ninguém mais pode entrar com ele.');
    renderInviteCard();
  } catch (e) {
    toastError('Erro ao revogar código: ' + (e instanceof Error ? e.message : String(e)));
  }
}

export function copyInviteCode(): void {
  const code = getTrip().inviteCode;
  if (!code) return;
  navigator.clipboard.writeText(code).then(
    () => toastSuccess('Código copiado!'),
    () => toastError('Não foi possível copiar. Copie manualmente: ' + code),
  );
}

export async function joinWithCode(): Promise<void> {
  const { openModal } = await import('@/ui/modal');
  openModal('Entrar com código de convite', `
    <div class="fg">
      <label>Código de convite</label>
      <input id="f-invite-code" placeholder="Ex: ABCD-EFGH"
        style="text-transform:uppercase;letter-spacing:2px;font-family:monospace;font-size:16px"
        oninput="this.value=this.value.toUpperCase()">
    </div>
    <div style="font-size:12px;color:var(--muted);margin-top:4px">
      Peça o código a quem criou a viagem.
    </div>`,
    async () => {
      const raw = (document.getElementById('f-invite-code') as HTMLInputElement)?.value.trim();
      if (!raw) throw new Error('Insira o código de convite.');

      const invite = await resolveInvite(raw);
      if (!invite) throw new Error('Código inválido ou expirado. Verifique e tente novamente.');

      const { currentUser } = getState();
      if (!currentUser) throw new Error('Você precisa estar logado.');

      await joinTripViaInvite(invite);

      logAction('invite_used', `Entrou na trip via código: ${invite.code}`, {
        tripId: invite.tripId,
        code: invite.code,
      });

      toastSuccess(`Você entrou na viagem "${invite.tripName}"!`);

      const { loadTrips } = await import('@/screens/trips.screen');
      loadTrips();
    }
  );
}
